import { supabase } from '../lib/supabase';
import { toLocaleDateStringInTz } from '../utils/dateFormatters';
import { getLocalUser } from '../utils/authHelpers';

interface LeadData {
  [key: string]: any;
}

interface MeetingData {
  meeting_id: string;
  meeting_date: string;
  meeting_time: string;
  meeting_status: string;
  sales_person: string;
  booked_channel: string;
  transcript: string | null;
  recording: string | null;
  meeting_link: string | null;
  lead_id: string;
}

interface DealData {
  deal_id: string;
  pipeline_stage: string;
  deal_value: number | null;
  sales_person: string | null;
  channel_booked: string | null;
  created_at: string;
  updated_at: string;
  lead_id: string;
}

interface CallLogData {
  call_outcome: string;
  call_attempt: number;
  call_summary: string | null;
  call_transcript: string | null;
  created_at: string;
}

interface SalesMaterialsData {
  about_company: any;
  pitch_book: any;
  onboarding_input: any;
}

interface AgentSettingsData {
  agent_type: string;
  agents_identity: any;
  agent_rules: any;
  templates: any;
}

async function fetchLead(leadId: string): Promise<LeadData | null> {
  const { data } = await supabase
    .from('leads_tracking')
    .select('*')
    .eq('lead_id', leadId)
    .maybeSingle();
  return data;
}

async function fetchMeeting(meetingId: string): Promise<MeetingData | null> {
  const { data } = await supabase
    .from('meetings_booked')
    .select('*')
    .eq('meeting_id', meetingId)
    .maybeSingle();
  return data;
}

async function fetchDeal(dealId: string): Promise<DealData | null> {
  const { data } = await supabase
    .from('deals_pipeline')
    .select('*')
    .eq('deal_id', dealId)
    .maybeSingle();
  return data;
}

async function fetchCallLogs(leadId: string): Promise<CallLogData[]> {
  const { data } = await supabase
    .from('call_logs')
    .select('call_outcome, call_attempt, call_summary, call_transcript, created_at')
    .eq('lead_id', leadId)
    .order('call_attempt', { ascending: true });
  return data || [];
}


async function fetchSalesMaterials(): Promise<SalesMaterialsData | null> {
  const localUser = getLocalUser();
  if (!localUser?.client_id) return null;

  const { data } = await supabase
    .from('sales_materials')
    .select('about_company, pitch_book, onboarding_input')
    .eq('client_id', localUser.client_id)
    .maybeSingle();
  return data;
}

async function fetchAgentSettings(): Promise<AgentSettingsData[]> {
  const localUser = getLocalUser();
  if (!localUser?.client_id) return [];

  const { data } = await supabase
    .from('agents_settings')
    .select('agent_type, agents_identity, agent_rules, templates')
    .eq('client_id', localUser.client_id);
  return data || [];
}

function formatLeadInsights(lead: LeadData): string {
  if (!lead.lead_insights || typeof lead.lead_insights !== 'string' || !lead.lead_insights.trim()) {
    return '';
  }

  const lines: string[] = [
    '',
    '--- LEAD INSIGHTS (AI-Generated Intelligence) ---',
    lead.lead_insights.trim(),
  ];
  return lines.join('\n');
}

function formatLeadProfile(lead: LeadData): string {
  const lines: string[] = [];

  lines.push('--- COMPANY PROFILE ---');
  if (lead.company_name) lines.push(`Company: ${lead.company_name}`);
  if (lead.industry_1) lines.push(`Industry: ${lead.industry_1}${lead.industry_2 ? `, ${lead.industry_2}` : ''}`);
  if (lead.company_country) lines.push(`Country: ${lead.company_country}`);
  if (lead.employee_headcount) lines.push(`Employees: ${lead.employee_headcount}`);
  if (lead.company_type) lines.push(`Type: ${lead.company_type}`);
  if (lead.products_services) lines.push(`Products/Services: ${lead.products_services}`);
  if (lead.company_website) lines.push(`Website: ${lead.company_website}`);
  if (lead.company_linkedin) lines.push(`LinkedIn: ${lead.company_linkedin}`);

  lines.push('');
  lines.push('--- DECISION MAKER ---');
  if (lead.dm_full_name) lines.push(`Name: ${lead.dm_full_name}`);
  if (lead.dm_title) lines.push(`Title: ${lead.dm_title}`);
  if (lead.dm_email_1) lines.push(`Email: ${lead.dm_email_1}`);
  if (lead.dm_mobile_1) lines.push(`Mobile: ${lead.dm_mobile_1}`);
  if (lead.dm_linkedin) lines.push(`LinkedIn: ${lead.dm_linkedin}`);

  return lines.join('\n');
}

function formatEngagementHistory(lead: LeadData): string {
  const lines: string[] = [];

  lines.push('--- OVERALL STATUS ---');
  if (lead.ovrall_lead_status) lines.push(`Overall Status: ${lead.ovrall_lead_status}`);
  if (lead.ovrall_lead_temp) lines.push(`Overall Temperature: ${lead.ovrall_lead_temp}`);
  if (lead.ovrall_convo_summary) lines.push(`Overall Summary: ${lead.ovrall_convo_summary}`);
  if (lead.meeting_booked) lines.push(`Meeting Booked: ${lead.meeting_booked}`);
  if (lead.deal_created) lines.push(`Deal Created: ${lead.deal_created}`);

  if (lead.sent_to_ca) {
    lines.push('');
    lines.push('--- CALL AGENT ---');
    if (lead.ca_lead_status) lines.push(`Status: ${lead.ca_lead_status}`);
    if (lead.ca_lead_temp) lines.push(`Temperature: ${lead.ca_lead_temp}`);
    if (lead.ca_call_attempts) lines.push(`Call Attempts: ${lead.ca_call_attempts}`);
    if (lead.ca_call_answered) lines.push(`Calls Answered: ${lead.ca_call_answered}`);
    if (lead.ca_last_call_outcome) lines.push(`Last Call Outcome: ${lead.ca_last_call_outcome}`);
    if (lead.ca_last_call_summary) lines.push(`Last Call Summary: ${lead.ca_last_call_summary}`);
    if (lead.ca_ovrall_calls_convo_summary) lines.push(`Overall Calls Summary: ${lead.ca_ovrall_calls_convo_summary}`);
    if (lead.ca_followup_status) lines.push(`Follow-up Status: ${lead.ca_followup_status}`);
    if (lead.ca_qualified) lines.push(`Qualified: ${lead.ca_qualified}`);
  }

  if (lead.sent_to_wa) {
    lines.push('');
    lines.push('--- WHATSAPP AGENT ---');
    if (lead.wa_lead_status) lines.push(`Status: ${lead.wa_lead_status}`);
    if (lead.wa_lead_temp) lines.push(`Temperature: ${lead.wa_lead_temp}`);
    if (lead.wa_messages_sent) lines.push(`Messages Sent: ${lead.wa_messages_sent}`);
    if (lead.wa_replies_received) lines.push(`Replies Received: ${lead.wa_replies_received}`);
    if (lead.wa_convo_outcome) lines.push(`Conversation Outcome: ${lead.wa_convo_outcome}`);
    if (lead.wa_convo_summary) lines.push(`Conversation Summary: ${lead.wa_convo_summary}`);
    if (lead.wa_conversation_thread) lines.push(`Conversation Thread:\n${lead.wa_conversation_thread}`);
    if (lead.wa_followup_status) lines.push(`Follow-up Status: ${lead.wa_followup_status}`);
    if (lead.wa_qualified) lines.push(`Qualified: ${lead.wa_qualified}`);
  }

  if (lead.sent_to_ea) {
    lines.push('');
    lines.push('--- EMAIL AGENT ---');
    if (lead.ea_lead_status) lines.push(`Status: ${lead.ea_lead_status}`);
    if (lead.ea_lead_temp) lines.push(`Temperature: ${lead.ea_lead_temp}`);
    if (lead.ea_emails_sent) lines.push(`Emails Sent: ${lead.ea_emails_sent}`);
    if (lead.ea_replies_received) lines.push(`Replies Received: ${lead.ea_replies_received}`);
    if (lead.ea_convo_outcome) lines.push(`Conversation Outcome: ${lead.ea_convo_outcome}`);
    if (lead.ea_convo_summary) lines.push(`Conversation Summary: ${lead.ea_convo_summary}`);
    if (lead.ea_email_thread) lines.push(`Email Thread:\n${lead.ea_email_thread}`);
    if (lead.ea_followup_status) lines.push(`Follow-up Status: ${lead.ea_followup_status}`);
    if (lead.ea_qualified) lines.push(`Qualified: ${lead.ea_qualified}`);
  }

  if (lead.sent_to_la) {
    lines.push('');
    lines.push('--- LINKEDIN AGENT ---');
    if (lead.la_lead_status) lines.push(`Status: ${lead.la_lead_status}`);
    if (lead.la_lead_temp) lines.push(`Temperature: ${lead.la_lead_temp}`);
    if (lead.la_cr_sent) lines.push(`Connection Request Sent: ${lead.la_cr_sent}`);
    if (lead.la_cr_accepted) lines.push(`Connection Accepted: ${lead.la_cr_accepted}`);
    if (lead.la_messages_sent) lines.push(`Messages Sent: ${lead.la_messages_sent}`);
    if (lead.la_replies_received) lines.push(`Replies Received: ${lead.la_replies_received}`);
    if (lead.la_convo_outcome) lines.push(`Conversation Outcome: ${lead.la_convo_outcome}`);
    if (lead.la_convo_summary) lines.push(`Conversation Summary: ${lead.la_convo_summary}`);
    if (lead.la_conversation_thread) lines.push(`Conversation Thread:\n${lead.la_conversation_thread}`);
    if (lead.la_followup_status) lines.push(`Follow-up Status: ${lead.la_followup_status}`);
    if (lead.la_qualified) lines.push(`Qualified: ${lead.la_qualified}`);
  }

  return lines.join('\n');
}

function formatCallLogs(logs: CallLogData[], tz?: string): string {
  if (logs.length === 0) return '';

  const lines: string[] = ['', '--- CALL HISTORY ---'];
  logs.forEach((log) => {
    lines.push(`\nCall #${log.call_attempt} (${toLocaleDateStringInTz(log.created_at, tz)})`);
    lines.push(`  Outcome: ${log.call_outcome}`);
    if (log.call_summary) lines.push(`  Summary: ${log.call_summary}`);
  });
  return lines.join('\n');
}

function formatSalesMaterials(materials: SalesMaterialsData): string {
  const lines: string[] = ['', '--- YOUR PITCH BOOK ---'];

  const about = materials.about_company;
  if (about) {
    if (about.usp_statement) lines.push(`USP: ${about.usp_statement}`);
    if (about.products_services) lines.push(`Products/Services: ${about.products_services}`);
    if (about.pain_points?.length) {
      lines.push(`Pain Points You Solve: ${about.pain_points.map((p: any) => p.text).join('; ')}`);
    }
    if (about.solutions?.length) {
      lines.push(`Solutions: ${about.solutions.map((s: any) => s.text).join('; ')}`);
    }
    if (about.benefits?.length) {
      lines.push(`Key Benefits: ${about.benefits.map((b: any) => b.text).join('; ')}`);
    }
    if (about.prospect_questions?.length) {
      lines.push(`Common Prospect Questions: ${about.prospect_questions.map((q: any) => q.text).join('; ')}`);
    }
  }

  const pitch = materials.pitch_book;
  if (pitch) {
    if (pitch.hooks?.length) {
      lines.push(`\nHooks: ${pitch.hooks.map((h: any) => h.text).join('; ')}`);
    }
    if (pitch.engaging_questions?.length) {
      lines.push(`Engaging Questions: ${pitch.engaging_questions.map((q: any) => q.text).join('; ')}`);
    }
    if (pitch.qualifying_questions?.length) {
      lines.push(`Qualifying Questions: ${pitch.qualifying_questions.map((q: any) => q.text).join('; ')}`);
    }
    if (pitch.objections?.length) {
      lines.push('\nObjection Handling:');
      pitch.objections.forEach((obj: any) => {
        lines.push(`  "${obj.title}" -> ${obj.response}`);
      });
    }
  }

  return lines.join('\n');
}

function formatCompanyIdentity(materials: SalesMaterialsData): string {
  const lines: string[] = ['--- YOUR COMPANY ---'];
  const onboarding = materials.onboarding_input;
  const about = materials.about_company;

  if (onboarding) {
    if (onboarding.website_url) lines.push(`Website: ${onboarding.website_url}`);
    if (onboarding.linkedin_url) lines.push(`LinkedIn: ${onboarding.linkedin_url}`);
    if (onboarding.industry) lines.push(`Industry: ${onboarding.industry}`);
    if (onboarding.country) lines.push(`Country: ${onboarding.country}`);
    if (onboarding.products_services) lines.push(`Products/Services: ${onboarding.products_services}`);
  }

  if (about) {
    if (about.usp_statement) lines.push(`USP: ${about.usp_statement}`);
    if (about.products_services && !onboarding?.products_services) {
      lines.push(`Products/Services: ${about.products_services}`);
    }
  }

  return lines.length > 1 ? lines.join('\n') : '';
}

function formatAgentIdentities(agents: AgentSettingsData[]): string {
  if (!agents.length) return '';

  const channelLabels: Record<string, string> = {
    call: 'Call Agent',
    email: 'Email Agent',
    linkedin: 'LinkedIn Agent',
    whatsapp: 'WhatsApp Agent',
  };

  const lines: string[] = ['', '--- YOUR SALES AGENTS ---'];

  for (const agent of agents) {
    const label = channelLabels[agent.agent_type] || agent.agent_type;
    const identity = agent.agents_identity;
    if (!identity) continue;

    lines.push(`\n[${label}]`);
    if (identity.agent_name) lines.push(`  Name: ${identity.agent_name}`);
    if (identity.role) lines.push(`  Role: ${identity.role}`);
    if (identity.tone) lines.push(`  Tone: ${identity.tone}`);
    if (identity.objective) lines.push(`  Objective: ${identity.objective}`);
    if (identity.location) lines.push(`  Location: ${identity.location}`);
    if (identity.languages?.length) lines.push(`  Languages: ${identity.languages.join(', ')}`);

    const rules = agent.agent_rules;
    if (rules) {
      if (rules.do?.length) {
        lines.push(`  Do: ${rules.do.map((r: any) => r.text).join('; ')}`);
      }
      if (rules.dont?.length) {
        lines.push(`  Don't: ${rules.dont.map((r: any) => r.text).join('; ')}`);
      }
    }
  }

  return lines.join('\n');
}

function formatAgentTemplates(agents: AgentSettingsData[]): string {
  if (!agents.length) return '';

  const lines: string[] = ['', '--- ACTIVE OUTREACH TEMPLATES ---'];

  for (const agent of agents) {
    const tpl = agent.templates;
    if (!tpl) continue;

    switch (agent.agent_type) {
      case 'call': {
        const s1 = tpl.script_1;
        if (s1) {
          lines.push('\n[Call Script]');
          if (s1.introduction) lines.push(`  Intro: ${s1.introduction}`);
          if (s1.why_we_are_calling) lines.push(`  Why We Call: ${s1.why_we_are_calling}`);
          if (s1.engagement) lines.push(`  Engagement: ${s1.engagement}`);
          if (s1.qualification) lines.push(`  Qualification: ${s1.qualification}`);
          if (s1.next_steps) lines.push(`  Next Steps: ${s1.next_steps}`);
          if (s1.booking) lines.push(`  Booking: ${s1.booking}`);
        }
        if (tpl.objection_handling?.length) {
          lines.push('  Objections:');
          for (const obj of tpl.objection_handling.slice(0, 5)) {
            const resp = obj.response?.length > 150 ? obj.response.slice(0, 150) + '...' : obj.response;
            lines.push(`    "${obj.title}" -> ${resp}`);
          }
        }
        break;
      }
      case 'email': {
        lines.push('\n[Email Templates]');
        if (tpl.email_outreach_pitch) {
          lines.push(`  Pitch Subject: ${tpl.email_outreach_pitch.subject}`);
          lines.push(`  Pitch Body: ${tpl.email_outreach_pitch.body?.slice(0, 200)}...`);
        }
        const fuKeys = Object.keys(tpl).filter(k => k.startsWith('email_outreach_fu_'));
        for (const key of fuKeys.slice(0, 2)) {
          lines.push(`  ${key} Subject: ${tpl[key].subject}`);
        }
        break;
      }
      case 'linkedin': {
        lines.push('\n[LinkedIn Templates]');
        if (tpl.li_outreach_connection) lines.push(`  Connection: ${tpl.li_outreach_connection.text}`);
        if (tpl.li_outreach_intro) lines.push(`  Intro: ${tpl.li_outreach_intro.text}`);
        if (tpl.li_outreach_pitch) lines.push(`  Pitch: ${tpl.li_outreach_pitch.text}`);
        break;
      }
      case 'whatsapp': {
        lines.push('\n[WhatsApp Templates]');
        if (tpl.wa_outreach_first) lines.push(`  First Message: ${tpl.wa_outreach_first.text}`);
        const wfuKeys = Object.keys(tpl).filter(k => k.startsWith('wa_outreach_fu_cold_'));
        for (const key of wfuKeys.slice(0, 2)) {
          lines.push(`  ${key}: ${tpl[key].text?.slice(0, 150)}...`);
        }
        break;
      }
    }
  }

  return lines.join('\n');
}

export async function buildMeetingPrepContext(leadId: string, meetingId: string, tz?: string): Promise<string> {
  const [lead, meeting, callLogs, materials, agents] = await Promise.all([
    fetchLead(leadId),
    fetchMeeting(meetingId),
    fetchCallLogs(leadId),
    fetchSalesMaterials(),
    fetchAgentSettings(),
  ]);

  if (!lead || !meeting) return '';

  const parts: string[] = [
    `CONTEXT: The user wants to prepare for an upcoming meeting.`,
    `Meeting Date: ${meeting.meeting_date} at ${meeting.meeting_time}`,
    `Booked Via: ${meeting.booked_channel || 'Unknown'}`,
    '',
  ];

  if (materials) {
    const companyId = formatCompanyIdentity(materials);
    if (companyId) parts.push(companyId, '');
  }

  parts.push(formatLeadProfile(lead));
  parts.push(formatLeadInsights(lead));
  parts.push('');
  parts.push(formatEngagementHistory(lead));
  parts.push(formatCallLogs(callLogs, tz));

  if (materials) {
    parts.push(formatSalesMaterials(materials));
  }

  if (agents.length) {
    parts.push(formatAgentIdentities(agents));
    parts.push(formatAgentTemplates(agents));
  }

  parts.push('');
  parts.push('--- END OF CRM DATA ---');

  return parts.join('\n');
}

export async function buildMeetingFeedbackContext(
  leadId: string,
  meetingId: string,
  transcript?: string,
  tz?: string
): Promise<string> {
  const [lead, meeting, callLogs, materials, agents] = await Promise.all([
    fetchLead(leadId),
    fetchMeeting(meetingId),
    fetchCallLogs(leadId),
    fetchSalesMaterials(),
    fetchAgentSettings(),
  ]);

  if (!lead || !meeting) return '';

  const parts: string[] = [
    `CONTEXT: The user wants feedback on a meeting they conducted.`,
    `Meeting Date: ${meeting.meeting_date} at ${meeting.meeting_time}`,
    `Booked Via: ${meeting.booked_channel || 'Unknown'}`,
    '',
  ];

  if (materials) {
    const companyId = formatCompanyIdentity(materials);
    if (companyId) parts.push(companyId, '');
  }

  parts.push(formatLeadProfile(lead));
  parts.push(formatLeadInsights(lead));
  parts.push('');
  parts.push(formatEngagementHistory(lead));
  parts.push(formatCallLogs(callLogs, tz));

  if (materials) {
    parts.push(formatSalesMaterials(materials));
  }

  if (agents.length) {
    parts.push(formatAgentIdentities(agents));
    parts.push(formatAgentTemplates(agents));
  }

  if (transcript) {
    parts.push('');
    parts.push('--- MEETING TRANSCRIPT ---');
    parts.push(transcript);
  } else if (meeting.transcript) {
    parts.push('');
    parts.push(`--- MEETING TRANSCRIPT URL ---`);
    parts.push(meeting.transcript);
    parts.push('(The transcript URL is provided but the raw text may not be available. Ask the user to paste it if needed.)');
  }

  parts.push('');
  parts.push('--- END OF CRM DATA ---');

  return parts.join('\n');
}

export async function buildDealAdviceContext(leadId: string, dealId: string, tz?: string): Promise<string> {
  const [lead, deal, callLogs, materials, agents] = await Promise.all([
    fetchLead(leadId),
    fetchDeal(dealId),
    fetchCallLogs(leadId),
    fetchSalesMaterials(),
    fetchAgentSettings(),
  ]);

  if (!lead || !deal) return '';

  const meetingData = lead.meeting_id
    ? await fetchMeeting(lead.meeting_id)
    : null;

  const parts: string[] = [
    `CONTEXT: The user needs strategy advice for a deal in their pipeline.`,
    `Pipeline Stage: ${deal.pipeline_stage}`,
    `Deal Value: ${deal.deal_value ? `$${Number(deal.deal_value).toLocaleString()}` : 'Not set'}`,
    `Created: ${toLocaleDateStringInTz(deal.created_at, tz)}`,
    `Channel Booked: ${deal.channel_booked || 'Unknown'}`,
    '',
  ];

  if (materials) {
    const companyId = formatCompanyIdentity(materials);
    if (companyId) parts.push(companyId, '');
  }

  parts.push(formatLeadProfile(lead));
  parts.push(formatLeadInsights(lead));
  parts.push('');
  parts.push(formatEngagementHistory(lead));
  parts.push(formatCallLogs(callLogs, tz));

  if (meetingData) {
    parts.push('');
    parts.push('--- MEETING HISTORY ---');
    parts.push(`Meeting Date: ${meetingData.meeting_date} at ${meetingData.meeting_time}`);
    parts.push(`Meeting Status: ${meetingData.meeting_status}`);
    if (meetingData.transcript) {
      parts.push('');
      parts.push('--- MEETING TRANSCRIPT ---');
      parts.push(meetingData.transcript);
    }
  }

  if (materials) {
    parts.push(formatSalesMaterials(materials));
  }

  if (agents.length) {
    parts.push(formatAgentIdentities(agents));
    parts.push(formatAgentTemplates(agents));
  }

  parts.push('');
  parts.push('--- END OF CRM DATA ---');

  return parts.join('\n');
}

export async function buildLeadIntelContext(leadId: string, tz?: string): Promise<string> {
  const [lead, callLogs, materials, agents] = await Promise.all([
    fetchLead(leadId),
    fetchCallLogs(leadId),
    fetchSalesMaterials(),
    fetchAgentSettings(),
  ]);

  if (!lead) return '';

  const meetingData = lead.meeting_id
    ? await fetchMeeting(lead.meeting_id)
    : null;

  const dealData = lead.deal_id
    ? await fetchDeal(lead.deal_id)
    : null;

  const parts: string[] = [
    `CONTEXT: The user wants a complete intelligence briefing about a specific lead.`,
    '',
  ];

  if (materials) {
    const companyId = formatCompanyIdentity(materials);
    if (companyId) parts.push(companyId, '');
  }

  parts.push(formatLeadProfile(lead));
  parts.push(formatLeadInsights(lead));
  parts.push('');
  parts.push('--- LEAD JOURNEY ---');
  parts.push(`Lead Source: ${lead.lead_source || 'Unknown'}`);
  parts.push(`Created: ${lead.created_at ? toLocaleDateStringInTz(lead.created_at, tz) : 'Unknown'}`);
  parts.push(`Outreach Started: ${lead.outreach_start_timestamp ? toLocaleDateStringInTz(lead.outreach_start_timestamp, tz) : 'Not started'}`);
  parts.push(`Assigned to Call Agent: ${lead.sent_to_ca ? 'Yes' : 'No'}`);
  parts.push(`Assigned to WhatsApp Agent: ${lead.sent_to_wa ? 'Yes' : 'No'}`);
  parts.push(`Assigned to Email Agent: ${lead.sent_to_ea ? 'Yes' : 'No'}`);
  parts.push(`Assigned to LinkedIn Agent: ${lead.sent_to_la ? 'Yes' : 'No'}`);
  parts.push('');
  parts.push(formatEngagementHistory(lead));
  parts.push(formatCallLogs(callLogs, tz));

  if (meetingData) {
    parts.push('');
    parts.push('--- MEETING ---');
    parts.push(`Date: ${meetingData.meeting_date} at ${meetingData.meeting_time}`);
    parts.push(`Status: ${meetingData.meeting_status}`);
    parts.push(`Channel: ${meetingData.booked_channel}`);
  }

  if (dealData) {
    parts.push('');
    parts.push('--- DEAL ---');
    parts.push(`Stage: ${dealData.pipeline_stage}`);
    parts.push(`Value: ${dealData.deal_value ? `$${Number(dealData.deal_value).toLocaleString()}` : 'Not set'}`);
  }

  if (materials) {
    parts.push(formatSalesMaterials(materials));
  }

  if (agents.length) {
    parts.push(formatAgentIdentities(agents));
    parts.push(formatAgentTemplates(agents));
  }

  parts.push('');
  parts.push('--- INSTRUCTIONS ---');
  parts.push('Give a comprehensive intelligence briefing about this lead covering their full journey across all channels.');
  parts.push('Incorporate the Lead Insights (company insights, decision maker insights, outreach angles, personalization hooks) as foundational intelligence in your briefing.');
  parts.push('Structure your response as: Lead Journey Overview, Engagement Breakdown by Channel, Key Insights & Patterns, Current Status Assessment, and Recommended Next Steps.');
  parts.push('Then ask: Would you like me to draft a follow-up message for a specific channel? Do you want strategy advice for next steps?');

  return parts.join('\n');
}

export async function buildFollowUpStrategyContext(leadId: string, tz?: string): Promise<string> {
  const [lead, callLogs, materials, agents] = await Promise.all([
    fetchLead(leadId),
    fetchCallLogs(leadId),
    fetchSalesMaterials(),
    fetchAgentSettings(),
  ]);

  if (!lead) return '';

  const meetingData = lead.meeting_id
    ? await fetchMeeting(lead.meeting_id)
    : null;

  const dealData = lead.deal_id
    ? await fetchDeal(lead.deal_id)
    : null;

  const parts: string[] = [
    `CONTEXT: The user wants follow-up strategy coaching for a specific lead.`,
    '',
  ];

  if (materials) {
    const companyId = formatCompanyIdentity(materials);
    if (companyId) parts.push(companyId, '');
  }

  parts.push(formatLeadProfile(lead));
  parts.push(formatLeadInsights(lead));
  parts.push('');
  parts.push('--- LEAD JOURNEY ---');
  parts.push(`Lead Source: ${lead.lead_source || 'Unknown'}`);
  parts.push(`Created: ${lead.created_at ? toLocaleDateStringInTz(lead.created_at, tz) : 'Unknown'}`);
  parts.push(`Outreach Started: ${lead.outreach_start_timestamp ? toLocaleDateStringInTz(lead.outreach_start_timestamp, tz) : 'Not started'}`);
  parts.push(`Assigned to Call Agent: ${lead.sent_to_ca ? 'Yes' : 'No'}`);
  parts.push(`Assigned to WhatsApp Agent: ${lead.sent_to_wa ? 'Yes' : 'No'}`);
  parts.push(`Assigned to Email Agent: ${lead.sent_to_ea ? 'Yes' : 'No'}`);
  parts.push(`Assigned to LinkedIn Agent: ${lead.sent_to_la ? 'Yes' : 'No'}`);
  parts.push('');
  parts.push(formatEngagementHistory(lead));
  parts.push(formatCallLogs(callLogs, tz));

  if (meetingData) {
    parts.push('');
    parts.push('--- MEETING ---');
    parts.push(`Date: ${meetingData.meeting_date} at ${meetingData.meeting_time}`);
    parts.push(`Status: ${meetingData.meeting_status}`);
    parts.push(`Channel: ${meetingData.booked_channel}`);
  }

  if (dealData) {
    parts.push('');
    parts.push('--- DEAL ---');
    parts.push(`Stage: ${dealData.pipeline_stage}`);
    parts.push(`Value: ${dealData.deal_value ? `$${Number(dealData.deal_value).toLocaleString()}` : 'Not set'}`);
  }

  if (materials) {
    parts.push(formatSalesMaterials(materials));
  }

  if (agents.length) {
    parts.push(formatAgentIdentities(agents));
    parts.push(formatAgentTemplates(agents));
  }

  parts.push('');
  parts.push('--- END OF CRM DATA ---');

  return parts.join('\n');
}

export async function buildSalesScriptsContext(scriptType: string, leadId?: string, tz?: string): Promise<string> {
  const scriptLabels: Record<string, string> = {
    cold_call: 'Cold Call Script',
    followup_call: 'Follow-Up Call Script',
    email: 'Email Template',
    linkedin: 'LinkedIn Message',
    whatsapp: 'WhatsApp Message',
    meeting_agenda: 'Meeting Script / Agenda',
  };

  const [materials, agents] = await Promise.all([
    fetchSalesMaterials(),
    fetchAgentSettings(),
  ]);

  const parts: string[] = [
    `CONTEXT: The user wants a ${scriptLabels[scriptType] || scriptType} written for them.`,
    `Script Type: ${scriptType}`,
    '',
  ];

  if (leadId) {
    const [lead, callLogs] = await Promise.all([
      fetchLead(leadId),
      fetchCallLogs(leadId),
    ]);

    if (lead) {
      if (materials) {
        const companyId = formatCompanyIdentity(materials);
        if (companyId) parts.push(companyId, '');
      }

      parts.push(formatLeadProfile(lead));
      parts.push(formatLeadInsights(lead));
      parts.push('');
      parts.push(formatEngagementHistory(lead));
      parts.push(formatCallLogs(callLogs, tz));

      if (scriptType === 'meeting_agenda') {
        const meetingData = lead.meeting_id ? await fetchMeeting(lead.meeting_id) : null;
        const dealData = lead.deal_id ? await fetchDeal(lead.deal_id) : null;

        if (meetingData) {
          parts.push('');
          parts.push('--- MEETING ---');
          parts.push(`Meeting Date: ${meetingData.meeting_date} at ${meetingData.meeting_time}`);
          parts.push(`Meeting Status: ${meetingData.meeting_status}`);
          parts.push(`Booked Via: ${meetingData.booked_channel || 'Unknown'}`);
          if (meetingData.meeting_link) parts.push(`Meeting Link: ${meetingData.meeting_link}`);
        }

        if (dealData) {
          parts.push('');
          parts.push('--- DEAL ---');
          parts.push(`Pipeline Stage: ${dealData.pipeline_stage}`);
          parts.push(`Deal Value: ${dealData.deal_value ? `$${Number(dealData.deal_value).toLocaleString()}` : 'Not set'}`);
          if (dealData.channel_booked) parts.push(`Channel Booked: ${dealData.channel_booked}`);
        }
      }

      if (materials) {
        parts.push(formatSalesMaterials(materials));
      }

      if (agents.length) {
        parts.push(formatAgentIdentities(agents));
        parts.push(formatAgentTemplates(agents));
      }

      parts.push('');
      parts.push('--- END OF CRM DATA ---');

      return parts.join('\n');
    }
  }

  if (materials) {
    const companyId = formatCompanyIdentity(materials);
    if (companyId) parts.push(companyId, '');
    parts.push(formatSalesMaterials(materials));
  }

  if (agents.length) {
    parts.push(formatAgentIdentities(agents));
    parts.push(formatAgentTemplates(agents));
  }

  parts.push('');
  parts.push('--- END OF CRM DATA ---');

  return parts.join('\n');
}

export async function searchLeads(query: string): Promise<LeadData[]> {
  const { data } = await supabase
    .from('leads_tracking')
    .select('lead_id, company_name, dm_full_name, dm_title, ovrall_lead_status, ovrall_lead_temp, industry_1')
    .or(`company_name.ilike.%${query}%,dm_full_name.ilike.%${query}%`)
    .order('updated_at', { ascending: false })
    .limit(20);
  return data || [];
}

export async function fetchUpcomingMeetings(): Promise<any[]> {
  const { data: meetings } = await supabase
    .from('meetings_booked')
    .select('meeting_id, meeting_date, meeting_time, meeting_status, booked_channel, lead_id')
    .or('meeting_status.ilike.upcoming,meeting_status.ilike.rescheduled')
    .order('meeting_date', { ascending: true });

  if (!meetings?.length) return [];

  const leadIds = [...new Set(meetings.map(m => m.lead_id).filter(Boolean))];
  const { data: leads } = await supabase
    .from('leads_tracking')
    .select('lead_id, company_name, dm_full_name, dm_title')
    .in('lead_id', leadIds);

  const leadMap = new Map((leads || []).map(l => [l.lead_id, l]));

  return meetings.map(m => ({
    ...m,
    company_name: leadMap.get(m.lead_id)?.company_name || 'Unknown',
    dm_full_name: leadMap.get(m.lead_id)?.dm_full_name || 'Unknown',
    dm_title: leadMap.get(m.lead_id)?.dm_title || '',
  }));
}

export async function fetchAttendedMeetings(): Promise<any[]> {
  const { data: meetings } = await supabase
    .from('meetings_booked')
    .select('meeting_id, meeting_date, meeting_time, meeting_status, booked_channel, lead_id, transcript')
    .ilike('meeting_status', 'attended')
    .order('meeting_date', { ascending: false });

  if (!meetings?.length) return [];

  const leadIds = [...new Set(meetings.map(m => m.lead_id).filter(Boolean))];
  const { data: leads } = await supabase
    .from('leads_tracking')
    .select('lead_id, company_name, dm_full_name, dm_title')
    .in('lead_id', leadIds);

  const leadMap = new Map((leads || []).map(l => [l.lead_id, l]));

  return meetings.map(m => ({
    ...m,
    company_name: leadMap.get(m.lead_id)?.company_name || 'Unknown',
    dm_full_name: leadMap.get(m.lead_id)?.dm_full_name || 'Unknown',
    dm_title: leadMap.get(m.lead_id)?.dm_title || '',
  }));
}

export async function buildBaselineCompanyContext(): Promise<string> {
  const localUser = getLocalUser();
  if (!localUser?.email) return '';

  const [materials, agents] = await Promise.all([
    fetchSalesMaterials(),
    fetchAgentSettings(),
  ]);

  if (!materials && !agents.length) return '';

  const parts: string[] = [];

  if (materials) {
    const companyId = formatCompanyIdentity(materials);
    if (companyId) parts.push(companyId);
    parts.push(formatSalesMaterials(materials));
  }

  if (agents.length) {
    parts.push(formatAgentIdentities(agents));
    parts.push(formatAgentTemplates(agents));
  }

  return parts.join('\n');
}

export async function fetchActiveDeals(): Promise<any[]> {
  const { data: deals } = await supabase
    .from('deals_pipeline')
    .select('deal_id, pipeline_stage, deal_value, sales_person, channel_booked, lead_id, created_at')
    .not('pipeline_stage', 'in', '(closed_won,closed_lost)')
    .order('updated_at', { ascending: false });

  if (!deals?.length) return [];

  const leadIds = [...new Set(deals.map(d => d.lead_id).filter(Boolean))];
  const { data: leads } = await supabase
    .from('leads_tracking')
    .select('lead_id, company_name, dm_full_name, dm_title, ovrall_lead_temp')
    .in('lead_id', leadIds);

  const leadMap = new Map((leads || []).map(l => [l.lead_id, l]));

  return deals.map(d => ({
    ...d,
    company_name: leadMap.get(d.lead_id)?.company_name || 'Unknown',
    dm_full_name: leadMap.get(d.lead_id)?.dm_full_name || 'Unknown',
    dm_title: leadMap.get(d.lead_id)?.dm_title || '',
    ovrall_lead_temp: leadMap.get(d.lead_id)?.ovrall_lead_temp || '',
  }));
}
